import gsap from 'gsap';
import { SplitText } from 'gsap/SplitText';

gsap.registerPlugin(SplitText);

/**
 * Character dissolve: each character fades and blurs out, the next text fades
 * and sharpens in. `stagger` is the total spread across all characters, so a
 * swap takes the same time whatever the length of the text.
 */

export const textFade = { duration: 0.3, blur: 6, stagger: 0.12, ease: 'circ.out' };

/** How long `hideText` takes, so a caller can start it early enough to finish on a beat. */

export const hideDuration = textFade.duration + textFade.stagger;

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/** The running swap per element: its split and its tween. */

const swaps = new WeakMap();

/** Reduced motion keeps a plain cross-fade of the whole line: no split, no blur. */

function hidden() {
  if (reducedMotion.matches) return { opacity: 0 };

  return { opacity: 0, filter: `blur(${textFade.blur}px)` };
}

/**
 * Words keep their characters together, so a line never breaks mid-word while
 * split. `reduceWhiteSpace` is off because it turns non-breaking spaces into
 * plain ones: the split text would wrap differently and jump on revert.
 */

function split(element) {
  if (reducedMotion.matches) return { targets: element, revert() {} };

  const result = SplitText.create(element, { type: 'words,chars', reduceWhiteSpace: false });

  return { targets: result.chars, revert: () => result.revert() };
}

/** Kills a running swap on `element` and leaves its text fully shown. */

export function stopText(element) {
  const swap = swaps.get(element);

  swap?.tween.kill();
  swap?.parts.revert();
  swaps.delete(element);
  gsap.set(element, { clearProps: 'opacity,filter' });
}

function play(element, parts, vars) {
  const tween = gsap.to(parts.targets, {
    duration: textFade.duration,
    stagger: { amount: textFade.stagger },
    ...vars,
  });

  swaps.set(element, { parts, tween });
}

/** Dissolves the current text out, character by character; it stays hidden. */

export function hideText(element) {
  stopText(element);
  play(element, split(element), { ...hidden(), ease: textFade.ease });
}

/** Puts `text` in and dissolves it into view, character by character. */

export function showText(element, text) {
  stopText(element);
  element.textContent = text;

  const parts = split(element);

  gsap.set(parts.targets, hidden());
  play(element, parts, {
    opacity: 1,
    filter: 'blur(0px)',
    ease: textFade.ease,
    onComplete: () => stopText(element),
  });
}
